import dayjs from 'dayjs'
import { useGetCustomCdfUpsertDeleteFns } from 'features/dataLayers/customCdfs/useGetCustomCdfUpsertDeleteFns'
import { useGetGenerateDerivedCdfDataFn } from 'features/dataLayers/generation/useGetGenerateDerivedCdfDataFn'
import { useGetForecastComponentConfig } from 'features/fde/componentConfigAPI'
import { getTMinMethod } from 'features/fde/tminMethod/tminMethodCallback'
import {
  availableInspectionLayers,
  type InspectionLayerKey,
  inspectionLayerSpecification,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionLayers'
import {
  inspectionLifeValues,
  supportsInspectionLifeBasis,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionLifeValues'
import type { InspectionResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import {
  inspectionComponentId,
  inspectionProvenance,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import { useRolodexCache } from 'features/rolodex'
import isNil from 'lodash/isNil'
import { useEffect, useRef, useState } from 'react'
import {
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
} from 'types'

export function useInspectionLifeLayer(
  result: InspectionResult,
  assetModel: GeckoAssetModel | undefined,
  baseCDF: GeckoComponentDataFrame | undefined,
  inspection: GeckoInspection | undefined,
  layerKey: InspectionLayerKey = 'remaining_life'
) {
  const componentId = inspectionComponentId(result)
  const source = inspectionProvenance(result)
  const { entries: configs, isLoading } = useGetForecastComponentConfig(
    source.forecast_config_id || result.kind === 'inspection_view'
      ? (componentId ?? '')
      : null
  )
  const config = source.forecast_config_id
    ? configs.find((entry) => entry.id === source.forecast_config_id)
    : result.kind === 'inspection_view'
      ? configs.at(0)
      : undefined
  const method = getTMinMethod(
    assetModel,
    componentId ?? '',
    baseCDF,
    config?.data
  )
  const availableLayers = availableInspectionLayers(
    assetModel,
    componentId ?? '',
    config?.data,
    inspection,
    method
  ).filter((key) => !result.calculation_error || key !== 'remaining_life')
  const generate = useGetGenerateDerivedCdfDataFn()
  const cache = useRolodexCache()
  const { createCdfFn } = useGetCustomCdfUpsertDeleteFns(cache)
  const createRef = useRef(createCdfFn)
  createRef.current = createCdfFn
  const [layer, setLayer] = useState<GeckoComponentDataFrame>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setLayer(undefined)
    setFailed(false)
    if (
      !assetModel ||
      !baseCDF ||
      !inspection ||
      isLoading ||
      layerKey === 'thickness'
    )
      return
    if (
      (layerKey === 'remaining_life' && !source.as_of_date) ||
      (source.forecast_config_id && !config) ||
      (!isNil(source.forecast_config_generation) &&
        config?.generation !== source.forecast_config_generation) ||
      (layerKey === 'remaining_life' &&
        result.kind === 'remaining_life' &&
        isNil(result.calculation?.result.min_remaining_life_years))
    ) {
      setFailed(true)
      return
    }
    let cancelled = false
    const componentId = baseCDF.tags.component
    const specification = inspectionLayerSpecification(layerKey, method)
    if (
      !specification ||
      !specification.canCreateCdfData(
        assetModel,
        componentId,
        config?.data ?? {},
        inspection
      )
    ) {
      setFailed(true)
      return
    }
    if (layerKey === 'remaining_life' && !supportsInspectionLifeBasis(source)) {
      setFailed(true)
      return
    }
    void generate({
      cdfSpecification: specification,
      componentId,
      assetModel,
      baseCDF,
      selectedInspection: inspection,
      forecastComponentConfig: config?.data,
    })
      .then(({ array }) => {
        if (cancelled) return
        const elapsed = dayjs(source.as_of_date).diff(
          dayjs(inspection.data.date),
          'year',
          true
        )
        if (
          layerKey === 'remaining_life' &&
          (!Number.isFinite(elapsed) || elapsed < 0)
        ) {
          setFailed(true)
          return
        }
        const bounds = source.bin_bounds
        const values =
          layerKey === 'remaining_life' && result.calculation
            ? inspectionLifeValues(array, elapsed, result.calculation)
            : array.map((row, y) =>
                row.map((value, x) => {
                  if (
                    !Number.isFinite(value) ||
                    (bounds &&
                      (x < bounds.x_start ||
                        x >= bounds.x_end ||
                        y < bounds.y_start ||
                        y >= bounds.y_end))
                  )
                    return NaN
                  return layerKey === 'remaining_life'
                    ? Math.max(-10, value - elapsed)
                    : value
                })
              )
        if (!values) {
          setFailed(true)
          return
        }
        const raw = createRef.current(`docchat-life-${crypto.randomUUID()}`, {
          view: specification.view,
          source: specification.source,
          aggregation: baseCDF.data.params.aggregation,
          measurementUnit: specification.defaultMeasurementUnit,
          anglePlotSetting: baseCDF.data.params.angle_plot_setting,
          algorithm: baseCDF.data.params.algorithm,
          tminMethod: method,
          binning: baseCDF.data.binning,
          geometry: baseCDF.data.geometry,
          tags: baseCDF.tags,
          data: values,
        })
        setLayer(GeckoComponentDataFrame.CreateFromJSON(raw))
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [
    assetModel,
    baseCDF,
    inspection,
    config,
    generate,
    isLoading,
    result,
    source,
    layerKey,
    method,
  ])
  return { layer, failed, availableLayers }
}
